/**
 * v3.21-1709 — Ghost Deal Closing Packet Builder frontend panel.
 * Consumes the proven /api/closing-packet/* endpoints.  Admin-gated
 * (backend enforces).  Renders the exact 27-page PDF via blob URL.
 *
 * Usage: <ClosingPacketBuilder client={client} deal={deal} ownerships={ownerships} />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Eye, FilePlus, Send, AlertTriangle,
  ShieldCheck, Loader2, Download, Hash,
} from 'lucide-react';

const STATUS_TONE = {
  DRAFT:               'bg-slate-100 text-slate-700 border-slate-200',
  NEEDS_INPUT:         'bg-amber-100 text-amber-800 border-amber-300',
  READY_TO_GENERATE:   'bg-sky-100 text-sky-800 border-sky-300',
  GENERATED:           'bg-indigo-100 text-indigo-800 border-indigo-300',
  READY_FOR_SIGNATURE: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  SENT_FOR_SIGNATURE:  'bg-purple-100 text-purple-800 border-purple-300',
  PARTIALLY_SIGNED:    'bg-purple-100 text-purple-800 border-purple-300',
  FULLY_SIGNED:        'bg-emerald-100 text-emerald-800 border-emerald-300',
  CANCELLED:           'bg-slate-100 text-slate-600 border-slate-200',
  FAILED:              'bg-red-100 text-red-800 border-red-300',
  FAILED_TO_SEND:      'bg-red-100 text-red-800 border-red-300',
};

const REQUIRED_FIELD_SLUGS = new Set([
  'client1_full_name','client1_email','client1_mobile',
  'client_mailing_street','client_mailing_city',
  'client_mailing_state','client_mailing_zip',
  'deal_total_service_fee','deal_amount_due_at_signing',
  'deal_payment_method','primary_ownership_resort',
  'primary_ownership_account_number',
]);

export default function ClosingPacketBuilder({ client, deal, ownerships = [] }) {
  const [selectedOwnershipIds, setSelectedOwnershipIds] = useState(
    (ownerships || []).slice(0, 2).map(o => o.id)
  );
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overrideEdits, setOverrideEdits] = useState({});
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [signerDialog, setSignerDialog] = useState({ open: false, revision: null });
  const [signerEmails, setSignerEmails] = useState('');
  const [signerNames, setSignerNames] = useState('');
  const [sending, setSending] = useState(false);

  const buildDraft = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post('/closing-packet/draft', {
        client_id: client.id,
        deal_id:   deal?.id || null,
        selected_ownership_ids: selectedOwnershipIds,
      });
      setDraft(data);
      setOverrideEdits(data.overrides || {});
      await loadRevisions(data.draft_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build packet draft');
    } finally { setLoading(false); }
  }, [client?.id, deal?.id, selectedOwnershipIds]);

  const loadRevisions = useCallback(async (draftId) => {
    if (!draftId) return;
    try {
      const { data } = await api.get(`/closing-packet/draft/${draftId}/revisions`);
      const items = (data?.revisions || []).map(r => ({
        id: r.id, revision: r.revision, sha256: r.sha256,
        pages: r.template_pages || r.pages, status: r.status,
        agreement_id: r.agreement_id,
        signed_pdf_document_id: r.signed_pdf_document_id,
        audit_certificate_document_id: r.audit_certificate_document_id,
        signer_states: r.signer_states || {},
      }));
      setRevisions(items);
    } catch (e) {
      // Non-fatal — draft still usable
    }
  }, []);

  useEffect(() => { buildDraft(); /* eslint-disable-next-line */ }, [client?.id, deal?.id, selectedOwnershipIds.join(',')]);

  const completeness = draft?.completeness || { filled: 0, total: 0, needs_input: [] };
  const needsInputRequired = (completeness.needs_input || []).filter(n => n.reason === 'required and empty');
  const needsInputOptional = (completeness.needs_input || []).filter(n => n.reason !== 'required and empty');
  const status = draft?.status || 'DRAFT';

  const saveOverrides = async () => {
    if (!draft) return;
    setSavingOverrides(true);
    try {
      const { data } = await api.put(`/closing-packet/draft/${draft.draft_id}/overrides`,
        { overrides: overrideEdits });
      setDraft(d => ({ ...d, status: data.status, overrides: data.overrides, completeness: data.completeness }));
      toast.success('Overrides saved (packet-only, Hub SSOT unchanged)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save overrides');
    } finally { setSavingOverrides(false); }
  };

  const previewExactPdf = async () => {
    if (!draft) return;
    try {
      const resp = await api.get(`/closing-packet/draft/${draft.draft_id}/preview.pdf`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    }
  };

  const generateRevision = async () => {
    if (!draft) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/closing-packet/draft/${draft.draft_id}/generate`, {});
      toast.success(`Generated ${data.revision} · SHA-256 ${(data.sha256 || '').slice(0, 12)}… · ${data.pages}pp`);
      setRevisions(r => [{ id: data.revision_id, revision: data.revision,
                             sha256: data.sha256, pages: data.pages,
                             status: 'GENERATED', agreement_id: null }, ...r]);
      setDraft(d => ({ ...d, status: 'READY_FOR_SIGNATURE' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generate failed');
    } finally { setGenerating(false); }
  };

  const openSendDialog = (rev) => {
    setSignerDialog({ open: true, revision: rev });
    // Prefill with Client 1 identity if available
    const emails = [client?.email].filter(Boolean).join('\n');
    const names  = [client?.full_name].filter(Boolean).join('\n');
    setSignerEmails(emails);
    setSignerNames(names);
  };
  const performSend = async () => {
    const rev = signerDialog.revision;
    const emails = signerEmails.split('\n').map(s => s.trim()).filter(Boolean);
    const names  = signerNames.split('\n').map(s => s.trim()).filter(Boolean);
    if (!rev || emails.length === 0 || emails.length !== names.length) {
      toast.error('Enter matching signer emails and names (one per line)'); return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/closing-packet/revision/${rev.id}/send-for-signature`,
        { signer_emails: emails, signer_names: names });
      toast.success(`Sent · agreement ${data.agreement_id?.slice(0, 8)}…`);
      setSignerDialog({ open: false, revision: null });
      setRevisions(rs => rs.map(r => r.id === rev.id
        ? { ...r, agreement_id: data.agreement_id, status: 'SENT_FOR_SIGNATURE' } : r));
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Send failed';
      toast.error(detail);
    } finally { setSending(false); }
  };

  const humanField = (slug) => slug.replace(/_/g, ' ').replace(/^client1/, 'Client 1').replace(/^client2/, 'Client 2').replace(/^ts(\d+)/, 'TS #$1');

  return (
    <div className="space-y-4" data-testid="closing-packet-builder">
      {/* Header */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="h-4 w-4 text-indigo-600" /> Closing Packet Builder
              </h2>
              <p className="text-xs text-slate-500">
                Client · <span className="font-medium">{client?.full_name || '—'}</span>
                {deal && <> · Deal · <span className="font-medium">{deal.deal_number || deal.id?.slice(0, 8)}</span></>}
                {draft && <> · Template · <span className="font-mono text-[11px]">{draft.template_version}</span> · {draft.template_pages}pp</>}
              </p>
            </div>
            <Badge className={`border ${STATUS_TONE[status] || STATUS_TONE.DRAFT}`}
                   data-testid="packet-status-badge">{status}</Badge>
          </div>

          {/* Ownership selection */}
          {ownerships.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-slate-700 mb-1">Timeshare Ownership Records</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {ownerships.map(o => {
                  const on = selectedOwnershipIds.includes(o.id);
                  return (
                    <label key={o.id}
                           data-testid={`ownership-select-${o.id}`}
                           className={`flex items-start gap-2 p-2 rounded border text-xs cursor-pointer ${on ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'}`}>
                      <input type="checkbox" checked={on} onChange={() => setSelectedOwnershipIds(ids =>
                        ids.includes(o.id) ? ids.filter(x => x !== o.id) : [...ids, o.id])} className="mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{o.resort_name || 'Unnamed Resort'}</div>
                        <div className="text-slate-500 truncate">{o.developer} · {o.ownership_type} · {o.points_owned || '—'} pts</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              {selectedOwnershipIds.length > 2 && (
                <p className="text-[11px] text-amber-700 mt-1">Only the first 2 selected map to Timeshare #1 / #2.</p>
              )}
            </div>
          )}

          {/* Completeness */}
          <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
            <div className="flex-1 min-w-[140px] h-2 bg-slate-200 rounded">
              <div className="h-2 bg-indigo-500 rounded" style={{ width: `${completeness.percent_complete || 0}%` }} />
            </div>
            <div className="text-slate-600 font-mono" data-testid="packet-completeness">
              {completeness.filled} / {completeness.total} · {completeness.percent_complete || 0}%
            </div>
            <Button size="sm" variant="outline" onClick={previewExactPdf} disabled={!draft}
                    data-testid="preview-pdf-btn">
              <Eye className="h-3 w-3 mr-1" /> Preview Exact PDF
            </Button>
            <Button size="sm" onClick={generateRevision}
                    disabled={!draft || !completeness.ready_for_signature || generating}
                    data-testid="generate-packet-btn">
              {generating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FilePlus className="h-3 w-3 mr-1" />}
              Generate Closing Packet
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* NEEDS INPUT */}
      {needsInputRequired.length > 0 && (
        <Card className="border-amber-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4" /> Needs Input · required
            </h3>
            <div className="space-y-2" data-testid="needs-input-required">
              {needsInputRequired.map(n => (
                <div key={n.field} className="grid sm:grid-cols-3 gap-2 items-center">
                  <div className="text-xs">
                    <div className="font-medium text-slate-800">{humanField(n.field)}</div>
                    <div className="text-slate-500 text-[11px]">Source: {n.source}</div>
                  </div>
                  <div className="sm:col-span-2">
                    <Input value={overrideEdits[n.field] || ''}
                            onChange={e => setOverrideEdits(o => ({ ...o, [n.field]: e.target.value }))}
                            placeholder={`Enter ${humanField(n.field)}`}
                            data-testid={`override-${n.field}`}
                            className="h-8 text-xs" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[11px] text-slate-500">Packet-only override. Hub SSOT is not modified.</p>
              <Button size="sm" onClick={saveOverrides} disabled={savingOverrides}
                      data-testid="save-overrides-btn">
                {savingOverrides ? 'Saving…' : 'Save Overrides'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {needsInputOptional.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Optional fields still empty</h3>
            <div className="grid sm:grid-cols-2 gap-1 text-[11px] text-slate-500" data-testid="needs-input-optional">
              {needsInputOptional.map(n => (
                <div key={n.field}>{humanField(n.field)} <span className="text-slate-400">· {n.source}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revisions */}
      {revisions.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-indigo-600" /> Packet Revisions (immutable)
            </h3>
            <div className="space-y-2" data-testid="packet-revisions">
              {revisions.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-2 p-2 rounded border border-slate-200 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{r.revision} · {r.pages}pp</div>
                    <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1">
                      <Hash className="h-3 w-3" /> {r.sha256?.slice(0, 24)}…
                    </div>
                    {r.agreement_id && <div className="text-[11px] text-slate-500">Agreement: {r.agreement_id.slice(0, 12)}…</div>}
                    {r.signer_states && Object.keys(r.signer_states).length > 0 && (
                      <div className="text-[11px] text-slate-600 mt-1 space-y-0.5" data-testid={`signer-states-${r.id}`}>
                        {Object.entries(r.signer_states).map(([email, s]) => (
                          <div key={email}>
                            <span className="font-medium">{email}</span>: <span className="uppercase font-mono">{s?.state || '—'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`border ${STATUS_TONE[r.status] || STATUS_TONE.GENERATED}`}>{r.status}</Badge>
                    {r.status === 'GENERATED' && (
                      <Button size="sm" onClick={() => openSendDialog(r)}
                              data-testid={`send-signature-btn-${r.id}`}>
                        <Send className="h-3 w-3 mr-1" /> Send for Signature
                      </Button>
                    )}
                    {r.signed_pdf_document_id && (
                      <a href={`/api/closing-packet/documents/${r.signed_pdf_document_id}`} target="_blank" rel="noreferrer"
                          className="text-xs text-indigo-600 underline flex items-center gap-1">
                        <Download className="h-3 w-3" /> Signed Packet
                      </a>
                    )}
                    {r.audit_certificate_document_id && (
                      <a href={`/api/closing-packet/documents/${r.audit_certificate_document_id}`} target="_blank" rel="noreferrer"
                          className="text-xs text-indigo-600 underline flex items-center gap-1">
                        <Download className="h-3 w-3" /> Audit Certificate
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <div className="text-xs text-slate-500">Loading packet draft…</div>}

      {/* Send-for-signature confirmation */}
      <Dialog open={signerDialog.open} onOpenChange={(v) => setSignerDialog(d => ({ ...d, open: v }))}>
        <DialogContent className="max-w-lg" data-testid="send-signature-dialog">
          <DialogHeader><DialogTitle>Send for Signature — {signerDialog.revision?.revision}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="rounded border p-2 bg-slate-50">
              <div><b>Client:</b> {client?.full_name}</div>
              {deal && <div><b>Deal:</b> {deal.deal_number || deal.id?.slice(0, 8)}</div>}
              <div><b>Packet Revision:</b> {signerDialog.revision?.revision} · {signerDialog.revision?.pages}pp</div>
              <div className="font-mono text-[10px]"><b>SHA-256:</b> {signerDialog.revision?.sha256?.slice(0, 32)}…</div>
            </div>
            <div>
              <div className="font-medium mb-1">Signer names (one per line)</div>
              <Textarea rows={3} value={signerNames} onChange={e => setSignerNames(e.target.value)}
                        data-testid="signer-names-input" placeholder="Client 1 Name" />
            </div>
            <div>
              <div className="font-medium mb-1">Signer emails (one per line — MUST match names in order)</div>
              <Textarea rows={3} value={signerEmails} onChange={e => setSignerEmails(e.target.value)}
                        data-testid="signer-emails-input" placeholder="client1@example.com" />
              <p className="text-[11px] text-slate-500 mt-1">
                Adobe will email the signature request to these addresses.  Use test-safe addresses only.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignerDialog({ open: false, revision: null })}>Cancel</Button>
            <Button onClick={performSend} disabled={sending} data-testid="confirm-send-signature">
              {sending ? 'Sending…' : 'Confirm & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
